import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import error from '../assets/Tr3.jpg';
import Cookies from 'js-cookie';
import { FaHeart, FaTag, FaStore, FaMapMarkerAlt, FaEye, FaShareAlt } from 'react-icons/fa';
import '../styles/productsLiked.css'; // Import the CSS file
import formatUrl from '../utils/urlFormatter';
import DOMPurify from 'dompurify';
import { ProductGridSkeleton } from '../components/Skeletons'; // Import skeleton loaders
import ScrollToTopButton from '../components/ScrollToTopButton'; // Import Scroll to Top Button
import { handleLikeClick as likeHandler } from '../utils/likeHandler'; // Import like handler
import Notification from '../components/Notification'; // Import Notification component
import ProductFilters from '../components/ProductFilters';
import { CityContext } from '../contexts/CityContext';
import ProductService from '../services/productService';
import { UserContext } from '../contexts/UserContext';

const ProductsLiked = () => {
    const navigate = useNavigate();
    const { cities } = useContext(CityContext);
    const { redirectToLogin } = useContext(UserContext);
    
    const [likedProducts, setLikedProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });
    const [shouldRedirect, setShouldRedirect] = useState(false);
    
    // Filter state
    const [filters, setFilters] = useState({
        sortBy: '',
        condition: '',
        minPrice: '',
        maxPrice: '',
        city: '',
        negotiable: false,
        promotedOnly: false
    });
    const [allProducts, setAllProducts] = useState([]); // Store unfiltered products
    const [filteredProducts, setFilteredProducts] = useState([]); // Store filtered products

    // Filter products based on current filters
    const applyFilters = useCallback((productsToFilter, currentFilters) => {
        let filtered = [...productsToFilter];

        // Condition filter
        if (currentFilters.condition) {
            filtered = filtered.filter(product => 
                product.condition && product.condition.toLowerCase() === currentFilters.condition.toLowerCase()
            );
        }

        // Price range filter
        if (currentFilters.minPrice || currentFilters.maxPrice) {
            filtered = filtered.filter(product => {
                const price = parseFloat(product.price) || 0;
                const min = parseFloat(currentFilters.minPrice) || 0;
                const max = parseFloat(currentFilters.maxPrice) || Infinity;
                return price >= min && (max === Infinity || price <= max);
            });
        }

        // City filter
        if (currentFilters.city) {
            filtered = filtered.filter(product => 
                product.city_name && product.city_name.toLowerCase() === currentFilters.city.toLowerCase()
            );
        }

        // Negotiable filter
        if (currentFilters.negotiable) {
            filtered = filtered.filter(product => product.negotiable === true);
        }

        // Promoted only filter
        if (currentFilters.promotedOnly) {
            filtered = filtered.filter(product => product.promote_type && product.promote_type !== 'none' && product.promote_type !== null);
        }

        // Sort products
        if (currentFilters.sortBy) {
            switch (currentFilters.sortBy) {
                case 'newest':
                    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    break;
                case 'oldest':
                    filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                    break;
                case 'price_low':
                    filtered.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
                    break;
                case 'price_high':
                    filtered.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
                    break;
                case 'most_liked':
                    filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                    break;
                case 'most_viewed':
                    filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
                    break;
                default:
                    break;
            }
        }

        return filtered;
    }, []);

    // Handle filter changes
    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    // Apply filters when filters or allProducts change
    useEffect(() => {
        if (allProducts.length > 0) {
            const filtered = applyFilters(allProducts, filters);
            setFilteredProducts(filtered);
            setLikedProducts(filtered); // Update displayed products
        }
    }, [filters, allProducts, applyFilters]);

    const fetchLikedProducts = async () => {
        const token = Cookies.get('authToken');
        if (token) {
            try {
                const response = await fetch(`${config.backendUrl}/products/likes/user/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    // Ensure data is an array
                    const productsArray = Array.isArray(data) ? data : (data.results || data.products || []);
                    setAllProducts(productsArray); // Store unfiltered products
                    setFilteredProducts(productsArray); // Initialize filtered products
                    setLikedProducts(productsArray);
                } else {
                    console.error('Failed to fetch liked products');
                    setLikedProducts([]);
                }
            } catch (error) {
                console.error('An error occurred while fetching liked products:', error);
                setLikedProducts([]);
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
            setLikedProducts([]);
        }
    };

    const fetchLoggedInUser = async () => {
        const token = Cookies.get('authToken');
        if (token) {
            try {
                const response = await fetch(`${config.backendUrl}/users/me/`, {
                    headers: {
                        'Authorization': `Token ${token}`
                    }
                });
                if (response.ok) {
                    const userData = await response.json();
                    setLoggedInUser(userData);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        }
    };

    useEffect(() => {
        fetchLoggedInUser();
    }, []);

    useEffect(() => {
        fetchLikedProducts();
    }, []);

    // Check authentication and redirect if not logged in
    useEffect(() => {
        const token = Cookies.get('authToken');
        // Only check after initial load attempt (when loggedInUser is explicitly null and no token)
        if (!token && loggedInUser === null) {
            console.error('[ProductsLiked] User not authenticated, redirecting to home');
            setNotification({
                message: 'Vous devez être connecté pour voir vos favoris',
                type: 'error',
                header: 'Non authentifié'
            });
            setShouldRedirect(true);
            setTimeout(() => {
                navigate('/');
            }, 2000);
        }
    }, [loggedInUser, navigate]);

    const handleLikeClick = async (productId, isLikedByUser) => {
        // If unliking, remove from local list immediately for better UX
        if (isLikedByUser) {
            setLikedProducts(prev => prev.filter(p => p.id !== productId));
            setAllProducts(prev => prev.filter(p => p.id !== productId));
            setFilteredProducts(prev => prev.filter(p => p.id !== productId));
        }

        await likeHandler({
            productId,
            isLikedByUser,
            loggedInUser,
            setLikedProducts: (updater) => {
                if (typeof updater === 'function') {
                    setLikedProducts(prev => {
                        const newLikedProducts = updater(prev.map(p => p.id));
                        return prev.filter(p => newLikedProducts.includes(p.id));
                    });
                }
            },
            setProducts: (updater) => {
                // Keep this for compatibility but not used here
            },
            navigate,
            setNotification,
            redirectToLogin
        });

        // Refresh the list after unliking
        if (isLikedByUser) {
            setTimeout(() => {
                fetchLikedProducts();
            }, 500);
        }
    };

    const handleImageError = (e) => {
        console.error('Image failed to load:', e.target.src);
        e.target.src = error; // Set the fallback image
    };

    const createMarkup = (html) => {
        if (!html) return { __html: '' };
        return { __html: DOMPurify.sanitize(html) };
    };

    return (
        <div className="products-liked-page-container">
            <div className="products-liked-header">
                <h1>Vos Coups de Coeur</h1>
                <p className="products-liked-subtitle">Découvrez vos produits favoris</p>
            </div>

            {/* Add Product Filters */}
            {!loading && allProducts.length > 0 && (
                <ProductFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    productsCount={filteredProducts.length}
                    totalProducts={allProducts.length}
                />
            )}

            {loading ? (
                <ProductGridSkeleton /> // Show skeleton for liked products
            ) : likedProducts.length === 0 ? (
                <div className="products-liked-empty-state">
                    <div className="empty-state-content">
                        <div className="empty-state-icon-container">
                            <FaHeart className="empty-state-icon" />
                        </div>
                        <h3 className="empty-state-title">Aucun produit favori</h3>
                        <p className="empty-state-message">
                            Vous n'avez pas encore ajouté de produits à vos favoris.
                            Explorez notre catalogue et cliquez sur le cœur pour ajouter des produits à cette liste.
                        </p>
                        <button
                            className="empty-state-action-button"
                            onClick={() => navigate('/products/all')}
                        >
                            Explorer les produits
                        </button>
                    </div>
                </div>
            ) : (
                <div className="products-liked-list">
                    {likedProducts.map(product => {
                        const condition = product.condition;
                        const formattedPrice = new Intl.NumberFormat('fr-FR').format(product.price);

                        // Safely check if images exist and provide a fallback image
                        const productImage = product.images && product.images[0] ? formatUrl(product.images[0].url || product.images[0].image) : error;

                        return (
                            <div key={product.id} className="products-liked-item">
                                <div className="products-liked-image-container"
                                    onClick={() => navigate(`/products/view/${product.uuid}`)}>  {/* Use product.uuid */}                                    <img
                                        src={productImage}
                                        alt={product.name || 'Product Image'}
                                        className="products-liked-image"
                                        onError={handleImageError}
                                    />
                                    
                                    {/* Stats overlay */}
                                    <div className="products-liked-stats-overlay">
                                        <div className="stat-badge">
                                            <FaEye />
                                            <span>{product.views}</span>
                                        </div>
                                        <div 
                                            className="stat-badge stat-badge-clickable"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLikeClick(product.id, true);
                                            }}
                                            title="Retirer des favoris"
                                        >
                                            <FaHeart />
                                            <span>{product.likes}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="products-liked-info">
                                    <h3 className="products-liked-name"
                                        onClick={() => navigate(`/products/view/${product.uuid}`)}>  {/* Use product.uuid */}
                                        {product.name}
                                    </h3>
                                    
                                    {/* Tags row */}
                                    <div className="products-liked-tags">
                                        {condition && (
                                            <div className="tag tag-condition">
                                                <FaTag />
                                                <span>{condition}</span>
                                            </div>
                                        )}
                                        <div className="tag tag-location">
                                            <FaMapMarkerAlt />
                                            <span>{product.city_name}</span>
                                        </div>
                                        {product.category_name && (
                                            <div className="tag tag-category">
                                                <FaTag />
                                                <span>{product.category_name}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="products-liked-owner-info"
                                        onClick={() => navigate(`/user/corner/${product.owner_uuid || product.owner}/`)}>  {/* Use owner_uuid */}
                                        <FaStore className="products-liked-store-icon" />
                                        <span className="products-liked-owner-name">
                                            {product.owner_first_name && product.owner_last_name 
                                                ? `${product.owner_first_name} ${product.owner_last_name}`
                                                : product.owner_name || 'Vendeur'}
                                        </span>
                                    </div>

                                    <div className="products-liked-description"
                                        dangerouslySetInnerHTML={createMarkup(product.description)} />

                                    <div className="products-liked-price-row">
                                        <div className="products-liked-price-tag">
                                            <FaTag />
                                            <span className="products-liked-price">
                                                {product.currency} {formattedPrice}
                                            </span>
                                        </div>
                                        {product.negotiable && (
                                            <div className="negotiable-badge">
                                                <FaShareAlt style={{ transform: 'rotate(90deg)' }} />
                                                <span>Négociable</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={() => setNotification({ message: '', type: '', header: '' })}
                />
            )}

            <ScrollToTopButton />
        </div>
    );
};

export default ProductsLiked;